import { type ReactNode, useId, useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button, type ButtonProps } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  /** Repeats the verb: "Delete organization" / "Keep organization". */
  confirmLabel: string;
  cancelLabel: string;
  variant?: ButtonProps['variant'];
  /** Awaited; the dialog closes when it resolves and stays open, showing the error, when it rejects. */
  onConfirm: () => Promise<unknown>;
  /** Text for a failed action. Defaults to the error's own message. */
  errorMessage?: (error: unknown) => string;
  /** When set, the action stays disabled until this exact text is typed (deleting an organization). */
  requireText?: string;
  requireTextLabel?: string;
};

/**
 * Confirmation of a destructive or irreversible action, on `alert-dialog`. The action is awaited, so a
 * refusal from the server is shown in place instead of closing over it.
 */
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  onConfirm,
  errorMessage = (error) => (error instanceof Error ? error.message : String(error)),
  requireText,
  requireTextLabel,
}: ConfirmDialogProps) {
  const inputId = useId();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function change(next: boolean) {
    if (!next) {
      setTyped('');
      setError(null);
    }
    onOpenChange(next);
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      change(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={change}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {requireText ? (
          <div className="grid gap-2">
            <Label htmlFor={inputId}>{requireTextLabel}</Label>
            <Input
              id={inputId}
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          {/* A plain button, not AlertDialogAction: that one closes at once, before the action has answered. */}
          <Button
            variant={variant}
            disabled={busy || (requireText !== undefined && typed !== requireText)}
            onClick={() => void confirm()}
          >
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { ConfirmDialog, type ConfirmDialogProps };
